import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { useSelector } from "react-redux";
import { Paper, Box, Button } from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import { useIntl, FormattedNumber} from "react-intl";
import ShowColumnModal from "../../components/PickingList/ShowColumnModal";
import PickingListTabs from "../../components/PickingList/PickingListTabs";
import PickingListHeader from "./PickingListHeader";
import { GET_PICKING_LIST_ORDERS, GET_PICKINGLIST_STATISTICS } from "../../graphql/queries";
import {
  initialColumnValues,
  initialsearchValues,
} from "../../components/PickingList/helpers";
import { getSelectedSiteOrderGroupId } from "../../helpers/selectors";
import SimpleModalCompact from "../../components/common/ModalCompact/SimpleModalCompact";
import DateFull from "../../components/common/FormControl/DateFull";
import { FormattedDate } from "../../components/common";


const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  paper: {
    boxShadow: "0 6px 10px -4px rgba(0, 0, 0, 0.15)",
    borderRadius: 3,
  },
  paperBody: {
    padding: theme.spacing(2),
  },
}));

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getMonthRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    from: firstDay,
    until: lastDay,
  };
};


const PickingList = () => {
  const classes = useStyles();
  const intl = useIntl();
  const [columnModal, setColumnModal] = useState(false);
  const [statisticsModal, setStatisticsModal] = useState(false);
  const [showColumn, setShowColumn] = useState(initialColumnValues);

  const orderGroupId = useSelector(state => getSelectedSiteOrderGroupId(state));
  const [searchValues, setSearchValues] = useState(initialsearchValues);
  const [page, setPage] = useState(0);
  const [pickingListOrders, setPickingListOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  
  const [statistics, setStatistics] = useState(() => {

    const { from, until } = getMonthRange();
    return { from, until, result: [] };
  });

  

  const {
    sortField,
    pickingListOrderStatus,
    pageSize,
    offset,
    sortDesc,
  } = searchValues;

  const handleToggleColumn = e => {
    const { name, checked } = e.target;
    setShowColumn({ ...showColumn, [name]: checked });
  };

  const [
    getPickingListOrders,
    { data: pickingListData, loading },
  ] = useLazyQuery(GET_PICKING_LIST_ORDERS);

  const [getPickingListStatistics, { data: statisticsData, loading: loadingStatistics }] = useLazyQuery(GET_PICKINGLIST_STATISTICS);

  const fetchPickingListOrders = () => {
    const model = { ...searchValues, orderGroupId };
    getPickingListOrders({ variables: { model } });
  };

  useEffect(() => {
    fetchPickingListOrders();
  }, []);

  useEffect(() => {
    if (statisticsModal) {
      handleFetchStatistics();
    }
  }, [statisticsModal]);

  useEffect(() => {
    if (statisticsModal) {
      handleFetchStatistics();
    }
  }, [statistics.from, statistics.until]);

  useEffect(() => {
    if (
      pickingListData &&
      pickingListData.pickingList &&
      pickingListData.pickingList.pickingListOrders &&
      pickingListData.pickingList.pickingListOrders.items
    ) {
      setPickingListOrders(pickingListData.pickingList.pickingListOrders.items);
      setTotalCount(pickingListData.pickingList.pickingListOrders.totalCount);
    }
  }, [pickingListData]);

  useEffect(() => {
    fetchPickingListOrders();
  }, [sortField, pickingListOrderStatus, pageSize, offset, sortDesc]);


  // when statisticsData comes back, update result
  useEffect(() => {
    if (statisticsData?.pickingList?.getPickingListStatistics) {
      setStatistics(prev => ({
        ...prev,
        result: statisticsData.pickingList.getPickingListStatistics,
      }));
    }
  }, [statisticsData]);

  const handleFetchStatistics = () => {
    getPickingListStatistics({
      variables: {
        from: statistics.from,
        until: statistics.until,
      },
    });
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper} elevation={0}>
        <PickingListHeader
          setColumnModal={setColumnModal}
          fetchPickingListOrders={fetchPickingListOrders}
          showStatistics={setStatisticsModal}
        />
        <Box className={classes.paperBody}>
          <PickingListTabs
            showColumn={showColumn}
            searchValues={searchValues}
            setSearchValues={setSearchValues}
            pickingListOrders={pickingListOrders}
            setPickingListOrders={setPickingListOrders}
            totalCount={totalCount}
            setTotalCount={setTotalCount}
            fetchPickingListOrders={fetchPickingListOrders}
            loading={loading}
            page={page}
            setPage={setPage}
          />
        </Box>
      </Paper>
      {columnModal && (
        <ShowColumnModal
          open={columnModal}
          setOpen={setColumnModal}
          showColumn={showColumn}
          handleToggleColumn={handleToggleColumn}
        />
      )}
      <SimpleModalCompact
        fullScreen={false}
        open={statisticsModal}
        onClose={() => setStatisticsModal(false)}
        title={intl.formatMessage({ id: "admin.statistic" })}
      >
        {/* Date selection and fetch button */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          {intl.formatMessage({ id: "orderHistorySearch.from" })}:
    <DateFull
            setDate={(date) => setStatistics(prev => ({ ...prev, from: date }))}
            value={statistics.from}
          />
          {intl.formatMessage({ id: "orderHistorySearch.to" })}:
    <DateFull
            setDate={(date) => setStatistics(prev => ({ ...prev, until: date }))}
            value={statistics.until}
          />
          <Button
            variant="contained"
            onClick={handleFetchStatistics}
          >
            {intl.formatMessage({ id: "btn.search" })}
          </Button>
        </Box>

        {/* Display date range for easy copy/paste */}
        <Box sx={{ mb: 2, fontWeight: 600 }}>
          <FormattedDate value={statistics.from}  /> {" - "}
          <FormattedDate value={statistics.until}  />
        </Box>

        {/* Stats Grid */}
        {statistics.result.length > 0 ? (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2, mt: 2 }}>
            {statistics.result.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  p: 2,
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  textAlign: "center",
                  boxShadow: 1,
                }}
              >
                <Box sx={{ fontWeight: 600 }}>{item.picker || "Okänd"}</Box>
                <Box>
                  {intl.formatMessage({ id: "orderHistory.weight" })}:{" "}
                  <FormattedNumber
                    value={item.weight || 0}
                    minimumFractionDigits={1}
                    maximumFractionDigits={1}
                    style="decimal"
                  /> kg
          </Box>
                <Box>
                  Antal: {item.count}
                </Box>
              </Box>
            ))}

            {/* Summary box */}
            <Box
              sx={{
                p: 2,
                border: "2px solid #444",
                borderRadius: 2,
                textAlign: "center",
                bgcolor: "#f5f5f5",
                boxShadow: 2,
              }}
            >
              <Box sx={{ fontWeight: 700 }}>Alla</Box>
              <Box>
                {intl.formatMessage({ id: "orderHistory.weight" })}:{" "}
                <FormattedNumber
                  value={statistics.result.reduce((sum, r) => sum + (r.weight || 0), 0)}
                  minimumFractionDigits={1}
                  maximumFractionDigits={1}
                  style="decimal"
                /> kg
        </Box>
              <Box>
                Antal:{" "}
                <FormattedNumber
                  value={statistics.result.reduce((sum, r) => sum + (r.count || 0), 0)}
                  style="decimal"
                />
              </Box>
            </Box>
          </Box>
        ) : (
            // No results message
            !loadingStatistics && (
              <Box sx={{ textAlign: "center", mt: 4, color: "#666" }}>
                {intl.formatMessage({ id: "search.noResultsFound" })}
              </Box>
            )
          )}
      </SimpleModalCompact>
     

    </div>
  );
};

export default PickingList;
